import { AppThunk } from '@store/types';
import { prependDemoMachine, removeDemoMachine } from '@reducers/machines';
import type { DemoTourConfig } from '@components/tours/types';
import { DEMO_WALLBOARD_MACHINE_ID, DEMO_OEE_VALUE } from './constants';

// Generate demo machine events for the batch timeline (colored segments)
const generateDemoMachineEvents = () => {
  const now = Date.now();
  const batchStartTime = now - 40 * 60 * 1000; // 40 minutes ago

  // Create realistic status change events
  return [
    {
      // Setup phase - purple (5 min)
      startedOn: new Date(batchStartTime).toISOString(),
      endedOn: new Date(batchStartTime + 5 * 60 * 1000).toISOString(),
      status: 'Setup',
    },
    {
      // Active phase 1 - green (12 min)
      startedOn: new Date(batchStartTime + 5 * 60 * 1000).toISOString(),
      endedOn: new Date(batchStartTime + 17 * 60 * 1000).toISOString(),
      status: 'Active',
    },
    {
      // Change phase - orange (3 min)
      startedOn: new Date(batchStartTime + 17 * 60 * 1000).toISOString(),
      endedOn: new Date(batchStartTime + 20 * 60 * 1000).toISOString(),
      status: 'Change',
    },
    {
      // Active phase 2 - green (15 min)
      startedOn: new Date(batchStartTime + 20 * 60 * 1000).toISOString(),
      endedOn: new Date(batchStartTime + 35 * 60 * 1000).toISOString(),
      status: 'Active',
    },
    {
      // Current active phase - green (5 min, ongoing)
      startedOn: new Date(batchStartTime + 35 * 60 * 1000).toISOString(),
      endedOn: null, // Currently running
      status: 'Active',
    },
  ];
};

// Demo batch statistics for realistic timelines and progress
const createDemoBatchStatistics = () => {
  const now = Date.now();
  const batchStartTime = now - 40 * 60 * 1000; // 40 minutes ago

  return {
    productionBatchId: 999999101,
    isInProgress: true,
    quantityReady: 12,
    quantityTarget: 25,

    // Batch start time (required for timeline duration calculation)
    startedOn: new Date(batchStartTime).toISOString(),

    // Productivity timeline targets (in seconds) - for current status (Active)
    idealActiveTimeAverageInSeconds: 720, // 12 minutes ideal per piece
    paidActiveTimeAverageInSeconds: 600, // 10 minutes paid
    scheduledActiveTimeAverageInSeconds: 540, // 9 minutes scheduled

    // Setup times
    idealSetupTimeInSeconds: 300,
    paidSetupTimeInSeconds: 240,
    scheduledSetupTimeInSeconds: 180,

    // Change times
    idealChangeTimeAverageInSeconds: 120,
    paidChangeTimeAverageInSeconds: 90,
    scheduledChangeTimeAverageInSeconds: 60,

    // Batch timeline totals (in seconds)
    actualTotalTimeTotalInSeconds: 2400, // 40 minutes running
    idealTargetTotalTimeTotalInSeconds: 3600, // 1 hour ideal
    paidTargetTotalTimeTotalInSeconds: 3000, // 50 minutes paid
    scheduledTargetTotalTimeTotalInSeconds: 2700, // 45 minutes scheduled

    // Links for pieces count
    links: [
      {
        productionOperationId: 999999105,
        quantityTarget: 25,
        productionBatchId: 999999101,
      },
    ],

    // Link status to prevent "Batch not linked to Job" message
    linkStatus: 'Linked',

    // Demo machine events for colored timeline segments
    demoMachineEvents: generateDemoMachineEvents(),
  };
};

// Demo machine for the wallboard tour
// Must have status = 'Active' and all required fields to show all tour elements
const demoMachine = {
  machineId: DEMO_WALLBOARD_MACHINE_ID,
  id: DEMO_WALLBOARD_MACHINE_ID,
  shortName: 'Demo CNC-01',
  fullName: 'Demo CNC Machine 01',
  machineStatus: {
    status: 'Active',
    subStatus: 'Running',
    ncFileStatus: 'Linked',
    isConnected: true,
    lastUpdatedOn: new Date(Date.now() - 4 * 60 * 1000).toISOString(), // 4 minutes ago (running time)
    targetDuration: 600, // 10 minutes
    productionBatchId: 999999101,
    ncFileId: 999999102,
    documentId: null, // No image - will show placeholder
    imageHash: null,
    ncFile: 'DEMO-PART-001.nc',
    partDetails: {
      partId: 999999103,
      partNumber: 'DEMO-PART-001',
      partDescription: 'Demo Part for Tour',
    },
    productionOrderDetails: {
      productionOrderId: 999999104,
      productionOrderNumber: 'DEMO-PO-001',
    },
  },
  // Demo batch data injected directly (used by item.js when present)
  demoBatch: createDemoBatchStatistics(),
  demoOee: DEMO_OEE_VALUE,
  // Additional machine properties
  machineGroups: [],
  operations: [],
};

export const injectWallboardDemo = (): AppThunk => (dispatch) => {
  // Prepend demo machine to machines list (will be first tile)
  dispatch(prependDemoMachine(demoMachine));
};

export const cleanupWallboardDemo = (): AppThunk => (dispatch) => {
  // Remove demo machine from machines list
  dispatch(removeDemoMachine(DEMO_WALLBOARD_MACHINE_ID));
};

// Export demo config for registry
export const demoConfig: DemoTourConfig = {
  tourId: 'wallboard-overview',
  inject: injectWallboardDemo,
  cleanup: cleanupWallboardDemo,
  injectDelay: 100,
};
